/**
 * 화면의 카드를 보이는 그대로 이미지로 만든다.
 *
 * 표지는 226권 중 223권이 search1.kakaocdn.net 에 있고, 이 서버는 CORS 헤더를
 * 주지 않는다. 그대로 캔버스에 그리면 캔버스가 오염돼 저장이 막힌다. 그래서
 * 찍기 직전에 표지를 우리 엣지 함수(cover-proxy)로 받아 data: 주소로 바꿔
 * 끼운다 — 찍는 사본에서만 바꾸고 화면은 그대로 카카오에서 받는다.
 * 프록시로도 못 받은 표지는 제목 블록으로 대신한다. 그림 하나 때문에 저장이
 * 막히면 안 된다.
 *
 * html2canvas 는 내보내기를 누를 때만 불러온다. 앱 첫 로딩을 무겁게 하지 않는다.
 */
import { supabase } from "../supabase";

const VOID = "#04050f";

/**
 * 인스타 규격. 찍은 카드를 크기를 맞춰 가운데 넣는다.
 * 스토리는 위아래 240px 쯤이 인스타 자기 UI(프로필·답장 칸)에 덮이므로
 * 카드와 서명을 그 안쪽에 둔다 (별자리·궤적 내보내기와 같은 기준).
 */
export const FORMATS = {
  feed: { key: "feed", label: "피드 4:5", w: 1080, h: 1350, top: 70, bottom: 130 },
  story: { key: "story", label: "스토리 9:16", w: 1080, h: 1920, top: 250, bottom: 320 },
};

/* data: 주소로 돌려준다. blob: 주소는 html2canvas 가 "blob:" 을 프로토콜로
   읽어 다른 출처로 판단하고 건너뛰어, 표지가 빈칸으로 찍혔다. */
async function proxied(src) {
  const { data, error } = await supabase.functions.invoke("cover-proxy", { body: { u: src } });
  if (error || !data?.data) throw new Error("cover");
  return `data:${data.type || "image/jpeg"};base64,${data.data}`;
}

/* 다 읽힐 때까지 기다린다. 끝내 못 읽어도 3초 뒤엔 넘어간다 — 그림 하나로
   저장이 멈추면 안 된다. */
function loaded(img) {
  return new Promise((res) => {
    if (img.complete && img.naturalWidth) { res(); return; }
    img.addEventListener("load", res, { once: true });
    img.addEventListener("error", res, { once: true });
    setTimeout(res, 3000);
  });
}

function sign(ctx, text, x, y, px) {
  ctx.font = `300 ${px}px 'Cormorant Garamond', serif`;
  ctx.fillStyle = "rgba(201,168,76,.75)";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  if ("letterSpacing" in ctx) ctx.letterSpacing = `${Math.round(px * 0.23)}px`;
  ctx.fillText(text, x, y);
}

/* 여백과 서명은 찍은 카드 바깥에 캔버스로 두른다. 찍는 사본에 padding 을
   주면 카드 폭이 좁아져 "읽었어/요" 처럼 줄이 다시 감긴다 — 화면과 달라진다. */
function frame(card, scale, footer) {
  const m = 18 * scale, top = 20 * scale, foot = footer ? 44 * scale : 16 * scale;
  const out = document.createElement("canvas");
  out.width = card.width + m * 2;
  out.height = card.height + top + foot;
  const ctx = out.getContext("2d");
  ctx.fillStyle = VOID;
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(card, m, top);
  if (footer) sign(ctx, footer, out.width / 2, top + card.height + 28 * scale, 13 * scale);
  return out;
}

/* 배경 별. 같은 그림이 늘 같게 나오도록 자리를 고정한다. */
function stars(ctx, w, h) {
  let s = 20260911;
  const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
  ctx.fillStyle = "#f5f0e8";
  for (let i = 0; i < 150; i++) {
    const x = rnd() * w, y = rnd() * h, r = 0.6 + rnd() * 1.4;
    ctx.globalAlpha = 0.1 + rnd() * 0.45;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/* 인스타 규격 판에 카드를 맞춰 넣는다. 늘리지는 않는다 — 흐려진다. */
function fitTo(card, f, footer) {
  const out = document.createElement("canvas");
  out.width = f.w;
  out.height = f.h;
  const ctx = out.getContext("2d");
  ctx.fillStyle = VOID;
  ctx.fillRect(0, 0, f.w, f.h);
  stars(ctx, f.w, f.h);

  const side = 60, boxW = f.w - side * 2, boxH = f.h - f.top - f.bottom;
  const k = Math.min(boxW / card.width, boxH / card.height, 1);
  const w = Math.round(card.width * k), h = Math.round(card.height * k);
  const x = Math.round((f.w - w) / 2), y = Math.round(f.top + (boxH - h) / 2);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(card, x, y, w, h);
  if (footer) sign(ctx, footer, f.w / 2, y + h + 64, 34);
  return out;
}

/**
 * @param {HTMLElement} el   찍을 요소. data-capture-root 를 달아둔다.
 *   안에서 data-no-capture="1" 인 것(화살표·버튼)은 빼고 찍는다.
 * @param {object} opts
 *   format   "feed" | "story" 면 그 규격 판에 넣는다. 없으면 카드 크기 그대로.
 *   prepare  (root, doc) => void. 찍는 사본만 손본다 (화면은 그대로).
 */
export default async function captureElement(el, { scale = 3, footer = "MUROO · muroo.kr", format, prepare } = {}) {
  const { default: html2canvas } = await import("html2canvas");

  const srcs = [...new Set([...el.querySelectorAll("img[data-cover]")].map((i) => i.getAttribute("src")))];
  const map = new Map();
  await Promise.all(srcs.map(async (s) => {
    try { map.set(s, await proxied(s)); } catch { map.set(s, null); }
  }));
  if (document.fonts?.ready) await document.fonts.ready.catch(() => {});

  /* 카드가 스크롤되는 시트 안에 있다. 내려가 있으면 html2canvas 가 화면 밖
     부분을 자르므로 시트를 맨 위로 올리고, 창 높이를 카드보다 넉넉히 준다. */
  el.closest(".mst-sheet-box")?.scrollTo(0, 0);
  const tall = Math.max(document.documentElement.clientHeight, el.scrollHeight + 200);

  try {
    const card = await html2canvas(el, {
      /* 투명하게 찍는다. 카드 바깥(달 제목 둘레)에 규격 판의 별이 비친다. */
      backgroundColor: null,
      scale,
      windowHeight: tall,
      scrollX: 0,
      scrollY: 0,
      logging: false,
      useCORS: false,
      allowTaint: false,
      ignoreElements: (n) => n?.dataset?.noCapture === "1",
      onclone: (doc) => {
        const root = doc.querySelector("[data-capture-root]");
        if (!root) return undefined;
        if (prepare) prepare(root, doc);
        const loads = [];
        /* 표지는 새 <img> 로 갈아끼운다. 기존 img 의 src 만 바꾸면 currentSrc 가
           아직 카카오 주소라 html2canvas 가 다른 출처로 보고 건너뛴다(빈칸). */
        root.querySelectorAll("img[data-cover]").forEach((img) => {
          const b = map.get(img.getAttribute("src"));
          if (b) {
            const n = doc.createElement("img");
            n.className = img.className;
            n.alt = img.alt;
            n.src = b;
            img.replaceWith(n);
            loads.push(loaded(n));
            return;
          }
          const d = doc.createElement("div");
          d.className = "mst-cv mst-cv-none";
          d.style.background = "#2e3f5c";
          d.textContent = img.getAttribute("alt") || "";
          img.replaceWith(d);
        });
        /* 갈아끼운 표지가 다 읽힐 때까지 기다린다. html2canvas 는 이미지를
           onclone 전에만 기다리고, 크기(naturalWidth)가 0 인 그림은 건너뛴다.
           크롬은 data: 를 거의 바로 읽어 대개 들어갔지만(그래도 가끔 빠짐),
           사파리 — 아이폰에서는 모든 브라우저 — 는 한 박자 늦어 표지가 전부 빠졌다. */
        return Promise.all(loads);
      },
    });
    return FORMATS[format] ? fitTo(card, FORMATS[format], footer) : frame(card, scale, footer);
  } finally {
    map.clear();
  }
}
